import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { ORDER_STATUS_LABEL_LOOKUP, SHIPPING_STATUS_LABEL_LOOKUP } from "../../constants"

import { PurchaseOrderTestResultsFilter } from "./PurchaseOrderTestResultsFilter"

import { StatusFilter } from "@/components/common/Filters/StatusFilter"
import { AerosDatePicker } from "@/components/UiKit"
import type { DateRange, OrderListFilter } from "@/graphql/codegen/graphql"
import { OrderListFilterSchema, OrderStatus, ShipStatus } from "@/graphql/codegen/graphql"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

const orderStatusList = Object.values(OrderStatus)
const shippingStatusList = Object.values(ShipStatus)

interface PurchaseOrdersFilterProps {
  routePath: string
}

export const PurchaseOrdersFilter: FC<PurchaseOrdersFilterProps> = ({ routePath }) => {
  const navigate = useNavigate({ from: routePath })
  const currentSearch = useSearch({ from: routePath })

  const { statuses, shipStatuses, start, end, outstandingTestResults } = currentSearch

  const methods = useForm<OrderListFilter>({
    values: {
      statuses,
      shipStatuses,
      outstandingTestResults,
    },
    defaultValues: OrderListFilterSchema().parse({
      statuses: [],
      shipStatuses: [],
      outstandingTestResults: false,
    }),
  })

  const handleFilterChange = useCallback(
    (name: keyof OrderListFilter | keyof DateRange, value: any) => {
      const params = getRefinedSearchParams(currentSearch, {
        ...currentSearch,
        [name]: value,
      })

      void navigate(params)
    },
    [currentSearch, navigate]
  )

  return (
    <FormProvider {...methods}>
      <Box className='h-full min-w-80'>
        <Box className='flex h-1/3 flex-1 flex-col'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            PO Status
          </Typography>
          <Divider />
          <Box className='flex-1 overflow-y-auto overflow-x-hidden'>
            <StatusFilter
              name='statuses'
              data={orderStatusList}
              control={methods.control}
              onFilterChange={handleFilterChange}
              labelMap={ORDER_STATUS_LABEL_LOOKUP}
            />
          </Box>
        </Box>
        {/* Shipping Status */}
        <Box className='flex h-1/4 flex-1 flex-col'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Shipping Status
          </Typography>
          <Divider />
          <Box className='flex-1 overflow-y-auto overflow-x-hidden'>
            <StatusFilter
              name='shipStatuses'
              data={shippingStatusList}
              control={methods.control}
              onFilterChange={handleFilterChange}
              labelMap={SHIPPING_STATUS_LABEL_LOOKUP}
            />
          </Box>
        </Box>
        <Box className='flex h-1/5 flex-1 flex-col'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Test Results
          </Typography>
          <Divider />
          <PurchaseOrderTestResultsFilter onFilterChange={handleFilterChange} />
        </Box>
        <Box className='flex h-1/5 flex-1 flex-col'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Sent Date Range
          </Typography>
          <Box className='flex gap-x-2 px-4'>
            <Box>
              <AerosDatePicker
                label='From'
                required
                name='poSentStartDate'
                value={start}
                onChange={(date) => handleFilterChange("start", date)}
              />
            </Box>
            <Box>
              <AerosDatePicker
                label='To'
                name='poSentEndDate'
                value={end}
                onChange={(date) => handleFilterChange("end", date)}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  )
}
